import React, { useState, useEffect } from "react";
import cloudApi from "../../../services/cloudApi";

const DeliveryHistoryModal = ({ provider, providerLabel, isOpen, onClose }) => {
  const [deliveries, setDeliveries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && provider) {
      loadHistory();
    }
  }, [isOpen, provider]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await cloudApi.getIntegrationDeliveries(provider, { limit: 50 });
      setDeliveries(response.deliveries || []);
      setTotal(response.total || 0);
    } catch (err) {
      console.error("Failed to load delivery history:", err);
      setError(err.message || "Failed to load delivery history");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getStatusDot = (status) => {
    switch (status) {
      case "success": return "bg-green-500";
      case "failed": return "bg-red-500";
      case "error": return "bg-orange-500";
      default: return "bg-gray-400";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "success": return "text-green-600 dark:text-green-400";
      case "failed": return "text-red-600 dark:text-red-400";
      case "error": return "text-orange-600 dark:text-orange-400";
      default: return "text-gray-500 dark:text-gray-400";
    }
  };

  const getEventLabel = (eventType) => {
    switch (eventType) {
      case "export.completed":
        return "Export";
      case "webhook.test":
        return "Test";
      case "sheets.export":
        return "Sheets Export";
      default:
        return eventType;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = cloudApi.toLocalDate(dateString);
    if (!date) return "N/A";
    return date.toLocaleString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDuration = (ms) => {
    if (!ms && ms !== 0) return "N/A";
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-200" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-xl max-h-[70vh] flex flex-col overflow-hidden border border-gray-200/50 dark:border-gray-700/50 animate-in slide-in-from-bottom-3 duration-200 ease-out"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-200/80 dark:border-gray-800 flex items-center justify-between">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white tracking-tight">Delivery Activity</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
              {providerLabel || "Integration"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:scale-105 active:scale-95"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center">
                <div className="relative">
                  <div className="w-8 h-8 rounded-full border-2 border-gray-200 dark:border-gray-700"></div>
                  <div className="absolute inset-0 w-8 h-8 rounded-full border-2 border-transparent border-t-indigo-500 animate-spin"></div>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400 mt-3">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <p className="text-sm text-gray-900 dark:text-white font-medium mb-1">Failed to load history</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 max-w-xs">{error}</p>
              <button
                onClick={loadHistory}
                className="px-3 py-1.5 text-xs font-medium bg-indigo-500 hover:bg-indigo-400 text-white rounded-lg transition-colors"
              >
                Retry
              </button>
            </div>
          ) : deliveries.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <p className="text-sm text-gray-900 dark:text-white font-medium mb-1">No deliveries yet</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 max-w-xs">Delivery attempts will appear here once an automation runs.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {deliveries.map((delivery) => (
                <div key={delivery.id} className="flex items-center gap-2 py-2.5 first:pt-0 last:pb-0 min-w-0 text-xs">
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${getStatusDot(delivery.status)}`} />
                  <span className={`font-medium capitalize flex-shrink-0 ${getStatusText(delivery.status)}`}>{delivery.status}</span>
                  <span className="text-gray-500 dark:text-gray-400 px-1.5 py-px bg-gray-100 dark:bg-gray-800 rounded font-medium flex-shrink-0">{getEventLabel(delivery.event_type)}</span>
                  {delivery.task_name && <span className="text-gray-400 dark:text-gray-500 truncate" title={delivery.task_name}>{delivery.task_name}</span>}
                  {delivery.error_message && <span className="text-red-500 dark:text-red-400 truncate font-mono" title={delivery.error_message}>{delivery.error_message}</span>}
                  <span className="flex-1" />
                  {delivery.row_count != null && <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">{delivery.row_count} {delivery.row_count === 1 ? "row" : "rows"}</span>}
                  {delivery.status_code != null && (
                    <span className={`font-mono tabular-nums flex-shrink-0 ${delivery.status_code >= 200 && delivery.status_code < 300 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                      {delivery.status_code}
                    </span>
                  )}
                  {delivery.duration_ms != null && <span className="text-gray-400 dark:text-gray-500 font-mono tabular-nums flex-shrink-0">{formatDuration(delivery.duration_ms)}</span>}
                  <span className="text-gray-400 dark:text-gray-500 font-mono tabular-nums flex-shrink-0">{formatDate(delivery.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && !error && deliveries.length > 0 && (
          <div className="px-5 py-3 border-t border-gray-200/80 dark:border-gray-800">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {deliveries.length}{total > deliveries.length ? ` of ${total}` : ""} deliver{deliveries.length !== 1 ? "ies" : "y"}
              </span>
              <button
                onClick={loadHistory}
                className="px-3 py-1.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-lg transition-colors text-xs font-medium flex items-center gap-1.5"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryHistoryModal;
